import {makeObservable, observable, runInAction} from 'mobx';

import type {
  CoreCoachGamificationRewardProfileConfirmSubscriptionParams,
  CoreCoachGamificationRewardProfileRefreshParams,
  CoreCoachGamificationRewardProfileSendSubscriptionInfoParams,
  CoreCoachGamificationRewardProfileUpdateParams,
} from '../types/core/methods';
import type {RewardProfileExpandedCommon} from './types';

import {isUserHasRole} from '../../utils/entity/user';
import {envManager} from '../../utils/env';
import {currentUserStore} from '../user';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';
import {expandObj} from '../utils/expand';
import {rewardProfileExpandCommon} from './expand';

export class RewardProfileStore<
  T extends RewardProfileExpandedCommon = RewardProfileExpandedCommon,
> extends EntityStore<T> {
  isSubscriptionExpired = false;
  rewardProfile: null | RewardProfileExpandedCommon = null;

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      isSubscriptionExpired: observable,
      rewardProfile: observable,
    });
  }

  async confirmSubscription() {
    await apiRequest<CoreCoachGamificationRewardProfileConfirmSubscriptionParams>(
      {
        method: 'coach.gamification.reward_profile.confirm_subscription',
      },
    );
  }

  async refresh() {
    try {
      if (envManager.get('isB2b') || !isUserHasRole('coach')) {
        return;
      }

      const result =
        await apiRequest<CoreCoachGamificationRewardProfileRefreshParams>({
          method: 'coach.gamification.reward_profile.refresh',
          params: {
            expand: rewardProfileExpandCommon,
            profiles: ['perks'],
          },
        });

      const rewardProfile = expandObj<T, RewardProfileExpandedCommon>(
        result[this._type],
        {
          expand: rewardProfileExpandCommon,
          expanded: result._expanded,
        },
      );

      runInAction(() => {
        this.rewardProfile = rewardProfile;

        this.isSubscriptionExpired = rewardProfile.payment_status === 'blocked';
      });
    } catch (error) {
      // do nothing
    }
  }

  async sendSubscriptionInfo() {
    if (
      this.rewardProfile &&
      this.rewardProfile.payment_status === 'early_adopter'
    ) {
      return;
    }

    await apiRequest<CoreCoachGamificationRewardProfileSendSubscriptionInfoParams>(
      {
        method: 'coach.gamification.reward_profile.send_subscription_info',
      },
    );
  }

  async updateRewardProfile(
    params: CoreCoachGamificationRewardProfileUpdateParams,
  ) {
    if (
      !(currentUserStore.user && currentUserStore.user.roles.includes('coach'))
    ) {
      return;
    }

    const result =
      await apiRequest<CoreCoachGamificationRewardProfileUpdateParams>({
        method: 'coach.gamification.reward_profile.update',
        params: {
          ...params,
          expand: rewardProfileExpandCommon,
        },
      });

    const rewardProfile = expandObj<T, RewardProfileExpandedCommon>(
      result[this._type],
      {
        expand: rewardProfileExpandCommon,
        expanded: result._expanded,
      },
    );

    runInAction(() => {
      this.rewardProfile = rewardProfile;

      this.isSubscriptionExpired = rewardProfile.payment_status === 'blocked';
    });
  }
}

export const rewardProfileStore = new RewardProfileStore('reward_profile', {});
